import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiMapPin, FiClock, FiBriefcase, FiCode, FiDatabase, FiLayers, FiTool, FiUsers, FiBarChart2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import MetaTags from '../components/SEO/MetaTags';

const JobCard = ({ title, department, location, type, description, requirements, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  let departmentIcon;
  switch (department) {
    case 'Engineering':
      departmentIcon = <FiCode />;
      break;
    case 'Data Science':
      departmentIcon = <FiDatabase />;
      break;
    case 'Product':
      departmentIcon = <FiLayers />;
      break;
    case 'Design':
      departmentIcon = <FiTool />;
      break;
    case 'HR & Operations':
      departmentIcon = <FiUsers />;
      break;
    case 'Marketing':
      departmentIcon = <FiBarChart2 />;
      break;
    default:
      departmentIcon = <FiBriefcase />;
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="glass-card p-6 rounded-xl"
    >
      <div className="flex justify-between mb-4">
        <div className="flex items-center text-primary-600 text-sm space-x-3">
          <span className="flex items-center">
            {departmentIcon}
            <span className="ml-1">{department}</span>
          </span>
          <span>•</span>
          <span className="flex items-center">
            <FiMapPin className="mr-1" />
            {location}
          </span>
          <span>•</span>
          <span className="flex items-center">
            <FiClock className="mr-1" />
            {type}
          </span>
        </div>
      </div>
      
      <h3 className="text-xl font-semibold mb-3">{title}</h3>
      
      <div className="mb-4 text-neutral-dark/80">
        <p>{description}</p>
      </div>
      
      <div className="mb-4">
        <h4 className="font-semibold mb-2">Key Requirements:</h4>
        <ul className="list-disc pl-5 space-y-1 text-neutral-dark/80">
          {requirements.map((req, idx) => (
            <li key={idx}>{req}</li>
          ))}
        </ul>
      </div>
      
      <div className="mt-4">
        <a 
          href={`mailto:careers@xdimension.ai?subject=Application for ${title} Position`}
          className="btn-primary inline-block"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = `mailto:careers@xdimension.ai?subject=Application for ${title} Position&body=Dear xdimension Team,%0A%0AI'm writing to apply for the ${title} position. Please find my resume attached.%0A%0AThank you for your consideration.`;
          }}
        >
          Apply Now
        </a>
      </div>
    </motion.div>
  );
};

const Positions = () => {
  // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const positions = [
    {
      title: "Senior AI Research Scientist",
      department: "Engineering",
      location: "Remote / San Francisco / London",
      type: "Full-time",
      description: "Join our AI research team to develop next-generation conversational and emotional intelligence models that power our Ainous AI companions. You'll be working on the cutting edge of natural language processing and human-AI interaction.",
      requirements: [
        "Ph.D. in Computer Science, Machine Learning, or related field",
        "5+ years of experience in AI research, particularly in NLP and dialogue systems",
        "Track record of publications in top AI conferences (NeurIPS, ICML, ACL, etc.)",
        "Experience with large language models like GPT, PaLM, or Llama",
        "Strong programming skills in Python and experience with deep learning frameworks"
      ]
    },
    {
      title: "Senior Hardware Engineer",
      department: "Engineering",
      location: "San Francisco / Hong Kong",
      type: "Full-time",
      description: "Lead the development of the physical embodiment of our AI companions, working at the intersection of robotics, industrial design, and human factors engineering. You'll help bring Ainous to life in a form that's both functional and beautiful.",
      requirements: [
        "Master's or Ph.D. in Electrical Engineering, Robotics, or related field",
        "7+ years of experience in consumer electronics hardware development",
        "Experience with sensor integration, power management, and thermal design",
        "Familiarity with manufacturing processes and supply chain management",
        "Strong interdisciplinary collaboration skills"
      ]
    },
    {
      title: "UX/UI Designer",
      department: "Design",
      location: "Remote / San Francisco / London / Hong Kong",
      type: "Full-time",
      description: "Design intuitive and delightful interfaces for our AI companion app and web experiences. You'll be responsible for creating user flows, wireframes, and high-fidelity mockups that make interacting with Ainous seamless and engaging.",
      requirements: [
        "5+ years of experience in product design for consumer technology",
        "Strong portfolio demonstrating user-centered design process",
        "Expertise in Figma, Sketch, or similar design tools",
        "Experience with motion design and interactive prototyping",
        "Knowledge of accessibility standards and inclusive design principles"
      ]
    },
    {
      title: "Machine Learning Engineer",
      department: "Data Science",
      location: "Remote / Singapore / Hong Kong / London",
      type: "Full-time",
      description: "Build and optimize the ML infrastructure that powers our AI companions. You'll work on making our models faster, more efficient, and ready for deployment in resource-constrained environments like our hardware devices.",
      requirements: [
        "Master's or higher in Computer Science, Machine Learning, or related field",
        "3+ years of experience deploying ML models to production",
        "Experience with model optimization techniques (quantization, pruning, distillation)",
        "Proficiency with PyTorch, TensorFlow, or similar frameworks",
        "Strong software engineering skills and DevOps experience"
      ]
    },
    {
      title: "Product Manager, AI Experiences",
      department: "Product",
      location: "San Francisco / London",
      type: "Full-time",
      description: "Shape the future of human-AI interaction as a product manager for our AI companion experiences. You'll work closely with research, engineering, and design teams to define product requirements and roadmaps for Ainous's capabilities.",
      requirements: [
        "5+ years of product management experience, preferably in AI/ML products",
        "Strong technical background with understanding of AI/ML concepts",
        "Experience shipping consumer hardware products is a plus",
        "Excellent communication and stakeholder management skills",
        "Data-driven approach to product decisions with strong analytical skills"
      ]
    },
    {
      title: "Technical Program Manager, Manufacturing",
      department: "HR & Operations",
      location: "Singapore / San Francisco / Hong Kong",
      type: "Full-time",
      description: "Oversee the manufacturing program for our AI companion hardware. You'll coordinate between design, engineering, and manufacturing partners to ensure high-quality production of Ainous devices at scale.",
      requirements: [
        "7+ years of technical program management experience in consumer electronics",
        "Experience managing relationships with contract manufacturers in Asia",
        "Knowledge of DFM, quality control processes, and supply chain management",
        "Strong project management skills and ability to manage complex timelines",
        "Excellent communication and problem-solving skills"
      ]
    }
  ];

  const departments = [
    "All Departments",
    "Engineering",
    "Data Science",
    "Product",
    "Design",
    "HR & Operations",
    "Marketing"
  ];

  return (
    <div className="min-h-screen">
      <MetaTags
        title="Open Positions | xdimension"
        description="Join our team at xdimension and help build the future of AI companionship. Explore our current job openings across engineering, design, product, and more."
        url="https://xdimension.ai/positions"
      />

      <div className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8">
            <FiArrowLeft className="mr-2" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center"
          >
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">Open <span className="text-gradient">Positions</span></h1>
            <p className="text-xl text-neutral-dark/80 max-w-3xl mx-auto">
              Join our team and help build the future of AI companionship. We're looking for passionate individuals who share our vision of creating technology that enhances human potential.
            </p>
          </motion.div>
          
          {/* Department Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-10 overflow-x-auto"
          >
            <div className="flex gap-3 pb-2">
              {departments.map((department, index) => (
                <button
                  key={index}
                  className={`whitespace-nowrap px-4 py-2 rounded-full transition-colors ${
                    department === "All Departments" 
                      ? "bg-primary-600 text-white" 
                      : "bg-white/50 backdrop-blur-sm hover:bg-primary-50"
                  }`}
                >
                  {department}
                </button>
              ))}
            </div>
          </motion.div>
          
          {/* Job Listings */}
          <div className="grid grid-cols-1 gap-6 mb-16">
            {positions.map((position, index) => (
              <JobCard key={index} {...position} index={index} />
            ))}
          </div>
          
          {/* No Suitable Position */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="glass-card p-8 md:p-12 rounded-2xl text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-6">Don't See a Suitable Position?</h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              We're always looking for exceptional talent. Even if you don't see a role that matches your skills, we'd love to hear from you.
            </p>
            <a 
              href="mailto:careers@xdimension.ai?subject=General Application"
              className="btn-primary"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = "mailto:careers@xdimension.ai?subject=General Application&body=Dear xdimension Team,%0A%0AI'm interested in exploring opportunities at xdimension. Please find my resume attached.%0A%0AThank you for your consideration.";
              }}
            >
              Send Your Resume
            </a>
          </motion.div>
          
          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="glass-card p-8 md:p-12 rounded-2xl"
          >
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-8 text-center">Why Work With Us</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <FiUsers className="text-primary-600 text-2xl" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Diverse & Inclusive Culture</h3>
                <p className="text-neutral-dark/80">
                  We're building a team as diverse as the users we serve, with inclusive practices embedded in our culture.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <FiBarChart2 className="text-primary-600 text-2xl" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Impact & Growth</h3>
                <p className="text-neutral-dark/80">
                  Work on technology that will transform how millions of people interact with AI, while growing your skills and career.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <FiLayers className="text-primary-600 text-2xl" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Comprehensive Benefits</h3>
                <p className="text-neutral-dark/80">
                  Competitive salary, equity, health insurance, flexible work options, and generous PTO policy.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <FiTool className="text-primary-600 text-2xl" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Cutting-Edge Technology</h3>
                <p className="text-neutral-dark/80">
                  Work with the latest in AI, robotics, and hardware technology, with access to tools and resources you need.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <FiCode className="text-primary-600 text-2xl" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Learning & Development</h3>
                <p className="text-neutral-dark/80">
                  Dedicated budget for conferences, courses, books, and other professional development opportunities.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <FiMapPin className="text-primary-600 text-2xl" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Global Opportunities</h3>
                <p className="text-neutral-dark/80">
                  With offices in San Francisco, Singapore, London, and Hong Kong, we offer opportunities to work and collaborate globally.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Positions;